import React from "react";
import { api } from "../../lib/api";
import { Plus, Trash2, Copy, Eye, EyeOff, Save, GripVertical, ChevronRight, ChevronDown, Sparkles, Image as ImageIcon, FileText, Type, LayoutGrid, BarChart3, Layers, ListChecks, Megaphone, Newspaper, HelpCircle, BookOpen, MoveVertical, Video } from "lucide-react";
import { toast } from "sonner";
import MediaPickerModal from "../../components/admin/MediaPickerModal";

const TYPE_ICON = { hero: LayoutGrid, text: Type, text_image: ImageIcon, parallax_image: ImageIcon, media_banner: ImageIcon, video_player: Video, workflow_steps: ListChecks, stats: BarChart3, services_grid: LayoutGrid, pricing_table: FileText, faq_list: HelpCircle, glossary_list: BookOpen, cta_band: Megaphone, news_teaser: Newspaper, manifest: Layers, brand_decoration: Sparkles };
const TYPE_LABEL = { hero: "Hero", text: "Text", text_image: "Text + Bild", parallax_image: "Parallax-Bild", media_banner: "Medien-Banner (Vollbild)", video_player: "Videoplayer", workflow_steps: "Workflow", stats: "Kennzahlen", services_grid: "Leistungen", pricing_table: "Preise", faq_list: "FAQ", glossary_list: "Glossar", cta_band: "CTA", news_teaser: "Aktuelles", manifest: "Manifest", brand_decoration: "Brand-Dekoration" };

const SECTION_TYPES = [
  { value: "brand_decoration", label: "Brand-Dekoration", desc: "Schwebendes Brand-Element (z.B. gelber Kreis) — Größe, Position, Höhe, Parallax editierbar" },
  { value: "hero", label: "Hero (Headline + Buttons + Medium)", desc: "Großer Einstieg mit Hintergrundbild, Video oder HTML-Animation" },
  { value: "media_banner", label: "Medien-Banner (Vollbild)", desc: "Vollbreites Video / HTML-Animation / Bild — optional mit H2-Headline (links / mittig / rechts) und Deckkraft-Regler" },
  { value: "video_player", label: "Videoplayer", desc: "Video mit Vorschaubild und Playbutton — Querformat, Hochformat oder quadratisch" },
  { value: "text", label: "Text-Block", desc: "Eyebrow + Headline + Fließtext" },
  { value: "text_image", label: "Text + Bild", desc: "Zwei-Spalten-Layout, Bild links oder rechts" },
  { value: "parallax_image", label: "Parallax-Bild", desc: "Vollbreites Bild mit Parallax-Effekt + Höhen-Slider" },
  { value: "workflow_steps", label: "Workflow-Schritte", desc: "Nummerierte Schritte (z.B. 7 Schritte)" },
  { value: "stats", label: "Kennzahlen", desc: "Dunkle Bühne mit 3–6 großen Zahlen" },
  { value: "services_grid", label: "Leistungen-Grid", desc: "Klickbare Service-Karten" },
  { value: "pricing_table", label: "Preistabelle (inline)", desc: "Schnelle Preisliste; größere Preise via /admin/pricing" },
  { value: "faq_list", label: "FAQ-Liste", desc: "Lädt automatisch aus FAQ-Bereich" },
  { value: "glossary_list", label: "Glossar-Liste", desc: "Lädt automatisch aus Glossar-Bereich" },
  { value: "cta_band", label: "CTA-Band", desc: "Gelbe Aufmerksamkeitsleiste mit Button" },
  { value: "news_teaser", label: "News-Teaser", desc: "Lädt die letzten Beiträge" },
  { value: "manifest", label: "Manifest", desc: "Werte/Prinzipien als nummerierte Liste" },
];

const Field = ({ label, value, onChange, multiline, type = "text" }) => (
  <div>
    <label className="brut-label">{label}</label>
    {multiline ? (
      <textarea rows={3} className="brut-textarea" value={value || ""} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <input type={type} className="brut-input" value={value || ""} onChange={(e) => onChange(e.target.value)} />
    )}
  </div>
);

function MediaPicker({ value, onChange, label = "Medium", filter = null }) {
  const [list, setList] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => { api.adminMedia().then(setList); }, []);
  const current = list.find(m => m.id === value);
  const thumb = current && (current.variants?.thumb || current.variants?.webp);
  return (
    <div>
      <label className="brut-label">{label}</label>
      <div className="flex items-center gap-3 flex-wrap">
        {thumb && (
          <img src={(process.env.REACT_APP_BACKEND_URL) + thumb} alt="" className="w-12 h-12 object-cover border-2 border-[var(--ink)] rounded-md shrink-0" />
        )}
        <button type="button" onClick={() => setOpen(true)} className="brut-btn brut-btn-ghost !py-2 !px-3 text-xs" data-testid="media-picker-open">Medium wählen</button>
        {value && <button type="button" onClick={() => onChange("")} className="text-xs underline">entfernen</button>}
        {current && <span className="font-mono text-xs opacity-70 truncate max-w-[160px]">{current.original_filename}</span>}
      </div>
      {open && (
        <MediaPickerModal
          filter={filter}
          onClose={() => setOpen(false)}
          onSelect={(m) => { onChange(m.id); setOpen(false); }}
        />
      )}
    </div>
  );
}

function SectionEditor({ sec, lang, onChange, onDelete, onDuplicate, expanded, onToggle, onDragHandleDown }) {
  const [localSec, setLocalSec] = React.useState(sec);
  const [dirty, setDirty] = React.useState(false);
  React.useEffect(() => { setLocalSec(sec); setDirty(false); }, [sec.id, sec.updated_at]);
  const content = localSec.content?.[lang] || {};
  const setContent = (patch) => { setLocalSec(s => ({ ...s, content: { ...(s.content || {}), [lang]: { ...content, ...patch } } })); setDirty(true); };
  const setBaseField = (patch) => { setLocalSec(s => ({ ...s, ...patch })); setDirty(true); };
  // Visual list helpers for structured section types (stats / workflow_steps / manifest)
  const updateList = (key, idx, patch) => { const arr = [...(content[key] || [])]; arr[idx] = { ...arr[idx], ...patch }; setContent({ [key]: arr }); };
  const addListItem = (key, item) => setContent({ [key]: [...(content[key] || []), item] });
  const removeListItem = (key, idx) => setContent({ [key]: (content[key] || []).filter((_, i) => i !== idx) });
  const moveListItem = (key, idx, dir) => { const arr = [...(content[key] || [])]; const tgt = idx + dir; if (tgt < 0 || tgt >= arr.length) return; [arr[idx], arr[tgt]] = [arr[tgt], arr[idx]]; setContent({ [key]: arr }); };
  const save = async () => { await onChange(localSec); setDirty(false); };
  // Auto-save when the section is collapsed (or another section is opened) so edits
  // are never silently lost if the user forgets to click "Speichern".
  const stateRef = React.useRef({ dirty, localSec });
  stateRef.current = { dirty, localSec };
  React.useEffect(() => {
    if (!expanded && stateRef.current.dirty) {
      onChange(stateRef.current.localSec);
      setDirty(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]);
  // Warn before closing/reloading the tab with unsaved changes.
  React.useEffect(() => {
    if (!dirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);
  const Icon = TYPE_ICON[localSec.type] || FileText;
  const previewTitle = content.headline || content.eyebrow || TYPE_LABEL[localSec.type] || localSec.type;
  const previewMeta = content.eyebrow ? `${TYPE_LABEL[localSec.type] || localSec.type} · ${content.eyebrow}` : (TYPE_LABEL[localSec.type] || localSec.type);

  return (
    <div className="admin-card overflow-hidden" data-testid={`section-editor-${localSec.id}`}>
      <div className="flex items-center gap-3 p-3 md:p-4">
        <button className="cursor-grab active:cursor-grabbing px-1 py-2 text-[var(--muted)] hover:text-[var(--ink)] touch-none select-none" aria-label="Ziehen zum Sortieren (vertikal)" data-drag-handle onMouseDown={onDragHandleDown} title="Zum vertikalen Verschieben hier ziehen">
          <GripVertical size={18} />
        </button>
        <button onClick={onToggle} className="p-1" aria-label="Aufklappen">
          {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>
        <div className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center" style={{ boxShadow: "var(--neu-raised-sm)" }}>
          <Icon size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold truncate flex items-center gap-2">
            {previewTitle}
            {dirty && <span className="text-[10px] font-bold uppercase tracking-wider text-orange-600">● ungespeichert</span>}
          </div>
          <div className="text-xs opacity-60 truncate">{previewMeta}</div>
        </div>
        {expanded && (
          <button onClick={save} className={`brut-btn !py-2 !px-3 text-xs ${dirty ? "" : "brut-btn-ghost"}`} data-testid={`save-section-${localSec.id}`}><Save size={14}/> Speichern</button>
        )}
        <button onClick={() => { setBaseField({ enabled: !localSec.enabled }); }} className={`w-9 h-9 rounded-full flex items-center justify-center ${localSec.enabled !== false ? "text-green-600" : "text-gray-400"}`} aria-label={localSec.enabled !== false ? "Sichtbar" : "Verborgen"}>
          {localSec.enabled !== false ? <Eye size={16}/> : <EyeOff size={16}/>}
        </button>
        <button onClick={onDuplicate} className="w-9 h-9 rounded-full flex items-center justify-center text-[var(--muted)] hover:text-[var(--ink)]" aria-label="Duplizieren"><Copy size={16}/></button>
        <button onClick={onDelete} className="w-9 h-9 rounded-full flex items-center justify-center text-red-600 hover:text-red-700" aria-label="Löschen"><Trash2 size={16}/></button>
      </div>

      {expanded && (
        <div className="p-5 md:p-6 border-t border-[var(--line)] bg-white/30">
          <div className="mb-5 p-3 md:p-4 rounded-xl border-2 border-[var(--ink)] bg-white flex flex-wrap items-center justify-between gap-3" data-testid={`section-save-bar-${localSec.id}`}>
            <div>
              <div className="font-extrabold">Diese Sektion bearbeiten</div>
              <div className={`text-xs font-bold ${dirty ? "text-orange-600" : "text-green-600"}`}>
                {dirty ? "● Änderungen noch nicht gespeichert" : "✓ Aktueller Stand ist gespeichert"}
              </div>
            </div>
            <button type="button" onClick={save} className="brut-btn !py-3 !px-5 text-sm" data-testid={`save-section-prominent-${localSec.id}`}>
              <Save size={16}/> DIESE SEKTION SPEICHERN
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Eyebrow / Section-Label" value={content.eyebrow} onChange={(v) => setContent({ eyebrow: v })} />
            <Field label="Headline" value={content.headline} onChange={(v) => setContent({ headline: v })} multiline />
            {["hero", "text", "text_image", "cta_band", "parallax_image", "media_banner", "video_player"].includes(localSec.type) && (
              <Field label="Body / Untertext" value={content.sub || content.body} onChange={(v) => setContent(localSec.type === "hero" ? { sub: v } : { body: v })} multiline />
            )}
            {["text", "text_image"].includes(localSec.type) && (
              <div data-testid="pad-y-slider-wrap">
                <label className="brut-label flex items-center justify-between">
                  <span>Sektionshöhe / Padding ({typeof localSec.pad_y === "number" ? `${localSec.pad_y}px` : "Standard"})</span>
                  {typeof localSec.pad_y === "number" && (
                    <button type="button" onClick={() => setBaseField({ pad_y: null })} className="text-[10px] underline opacity-70 hover:opacity-100" data-testid="pad-y-reset">Standard</button>
                  )}
                </label>
                <input type="range" min="0" max="160" step="4"
                  value={typeof localSec.pad_y === "number" ? localSec.pad_y : 80}
                  onChange={(e) => setBaseField({ pad_y: parseInt(e.target.value) })}
                  className="w-full" data-testid="pad-y-slider" />
                <span className="text-[10px] opacity-60 block mt-1">Kleiner = flacher. „Standard“ ≈ 80–112px (responsiv).</span>
              </div>
            )}
            {["hero", "cta_band", "text_image", "media_banner"].includes(localSec.type) && (
              <>
                <Field label="Primärer CTA-Text" value={content.primary_cta_label || content.cta_label} onChange={(v) => setContent(localSec.type === "hero" ? { primary_cta_label: v } : { cta_label: v })} />
                <Field label="Primärer CTA-Link" value={content.primary_cta_url || content.cta_url} onChange={(v) => setContent(localSec.type === "hero" ? { primary_cta_url: v } : { cta_url: v })} />
              </>
            )}
            {localSec.type === "hero" && (
              <>
                <Field label="Sekundärer CTA-Text" value={content.secondary_cta_label} onChange={(v) => setContent({ secondary_cta_label: v })} />
                <Field label="Sekundärer CTA-Link" value={content.secondary_cta_url} onChange={(v) => setContent({ secondary_cta_url: v })} />
              </>
            )}
            {localSec.type === "brand_decoration" && (
              <>
                <MediaPicker label="Brand-Medium" value={localSec.media_id} onChange={(v) => setBaseField({ media_id: v })} />
                <div>
                  <label className="brut-label">Position</label>
                  <select className="brut-select" value={content.position || "right"} onChange={(e) => setContent({ position: e.target.value })}>
                    <option value="right">Rechts</option><option value="left">Links</option><option value="center">Mittig</option>
                  </select>
                </div>
                <div><label className="brut-label">Größe ({content.size_vw || 38} vw)</label><input type="range" min="10" max="80" step="1" value={content.size_vw || 38} onChange={(e) => setContent({ size_vw: parseInt(e.target.value) })} className="w-full" /></div>
                <div><label className="brut-label">Höhe ({content.height_vh || 60} vh)</label><input type="range" min="20" max="100" step="5" value={content.height_vh || 60} onChange={(e) => setContent({ height_vh: parseInt(e.target.value) })} className="w-full" /></div>
                <div><label className="brut-label">Deckkraft ({(content.opacity ?? 1).toFixed(2)})</label><input type="range" min="0.1" max="1" step="0.05" value={content.opacity ?? 1} onChange={(e) => setContent({ opacity: parseFloat(e.target.value) })} className="w-full" /></div>
                <div><label className="brut-label">Parallax ({(localSec.parallax_factor ?? 0.25).toFixed(2)})</label><input type="range" min="0" max="0.6" step="0.05" value={localSec.parallax_factor ?? 0.25} onChange={(e) => setBaseField({ parallax_factor: parseFloat(e.target.value) })} className="w-full" /></div>
              </>
            )}
            {localSec.type === "video_player" && (
              <>
                <MediaPicker label="Video" filter="video" value={localSec.media_id} onChange={(v) => setBaseField({ media_id: v, media_mode: "video" })} />
                <MediaPicker label="Vorschaubild / Poster" filter="image" value={localSec.poster_media_id} onChange={(v) => setBaseField({ poster_media_id: v || null })} />
                <div>
                  <label className="brut-label">Videoformat</label>
                  <select className="brut-select" value={content.aspect || "landscape"} onChange={(e) => setContent({ aspect: e.target.value })}>
                    <option value="landscape">Querformat (16:9)</option>
                    <option value="portrait">Hochformat (9:16)</option>
                    <option value="square">Quadratisch (1:1)</option>
                  </select>
                </div>
                <Field label="Barrierefreier Videotitel" value={content.video_title} onChange={(v) => setContent({ video_title: v })} />
                <Field label="Bildunterschrift" value={content.caption} onChange={(v) => setContent({ caption: v })} multiline />
                <div className="md:col-span-2">
                  <Field label="Transkript (SEO / GEO / Barrierefreiheit)" value={content.transcript} onChange={(v) => setContent({ transcript: v })} multiline />
                </div>
              </>
            )}
            {["hero", "text_image", "parallax_image", "media_banner"].includes(localSec.type) && (
              <>
                <MediaPicker label="Medium" value={localSec.media_id} onChange={(v) => setBaseField({ media_id: v, media_id_en: null })} />
                <div>
                  <label className="brut-label">Medium-Modus</label>
                  <select className="brut-select" value={localSec.media_mode || ""} onChange={(e) => setBaseField({ media_mode: e.target.value || null })}>
                    <option value="">— auto —</option><option value="image">Bild</option><option value="video">Hintergrundvideo</option><option value="html_animation">HTML-Animation</option>
                  </select>
                </div>
                {["hero", "media_banner"].includes(localSec.type) && (
                  <div>
                    <label className="brut-label">Hintergrund-Deckkraft ({localSec.media_opacity ?? ((localSec.media_mode || "") === "html_animation" ? 70 : (localSec.type === "media_banner" ? 35 : 25))} %)</label>
                    <input type="range" min="0" max="100" step="1"
                      value={localSec.media_opacity ?? ((localSec.media_mode || "") === "html_animation" ? 70 : (localSec.type === "media_banner" ? 35 : 25))}
                      onChange={(e) => setBaseField({ media_opacity: parseInt(e.target.value) })}
                      className="w-full" data-testid="media-opacity-slider" />
                  </div>
                )}
                {["hero", "text_image", "parallax_image"].includes(localSec.type) && (
                  <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="media-bw-toggle">
                    <input type="checkbox" className="w-4 h-4 accent-[var(--accent)]" checked={localSec.media_bw !== false} onChange={(e) => setBaseField({ media_bw: e.target.checked })} />
                    <span className="brut-label !mb-0">Medium in Schwarzweiß anzeigen</span>
                  </label>
                )}
                {["hero", "parallax_image", "media_banner"].includes(localSec.type) && (
                  <div>
                    <label className="brut-label">{localSec.type === "hero" ? "Mindesthöhe" : "Höhe"} ({localSec.height_vh ? `${localSec.height_vh} vh` : "auto"})</label>
                    <input type="range" min={localSec.type === "hero" ? "0" : "30"} max="100" step="5" value={localSec.height_vh || (localSec.type === "hero" ? 0 : 70)} onChange={(e) => { const v = parseInt(e.target.value); setBaseField({ height_vh: (localSec.type === "hero" && v === 0) ? null : v }); }} className="w-full" data-testid="height-slider" />
                    {localSec.type === "hero" && <span className="text-[10px] opacity-60 block mt-1">0 = automatisch (inhaltsabhängig). Höher ziehen für mehr Platz, z. B. für HTML-Animationen.</span>}
                  </div>
                )}
                {localSec.type === "parallax_image" && (
                  <div><label className="brut-label">Parallax-Stärke ({(localSec.parallax_factor ?? 0.25).toFixed(2)})</label><input type="range" min="0" max="0.6" step="0.05" value={localSec.parallax_factor ?? 0.25} onChange={(e) => setBaseField({ parallax_factor: parseFloat(e.target.value) })} className="w-full" /></div>
                )}
                {localSec.type === "media_banner" && (
                  <div>
                    <label className="brut-label">Text-Ausrichtung</label>
                    <select className="brut-select" value={content.align || "center"} onChange={(e) => setContent({ align: e.target.value })}>
                      <option value="left">Links</option><option value="center">Mittig</option><option value="right">Rechts</option>
                    </select>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="mt-6 pt-4 border-t border-[var(--line)] flex items-center justify-end gap-3">
            <span className={`text-xs font-bold ${dirty ? "text-orange-600" : "text-green-600"}`}>
              {dirty ? "● Nicht gespeicherte Änderungen" : "✓ Keine erkannten Änderungen"}
            </span>
            {dirty && <button onClick={() => { setLocalSec(sec); setDirty(false); }} className="text-xs font-bold underline">Verwerfen</button>}
            <button onClick={save} className="brut-btn !py-2 !px-4 text-xs" data-testid={`save-section-bottom-${localSec.id}`}><Save size={14}/> Section speichern</button>
          </div>
          {/* Visual list editors for structured section types */}
          {["stats", "workflow_steps", "manifest", "services_grid"].includes(localSec.type) && (() => {
            const key = localSec.type === "workflow_steps" ? "steps" : "items";
            const list = content[key] || [];
            const label = localSec.type === "stats" ? "Kennzahlen" : localSec.type === "workflow_steps" ? "Workflow-Schritte" : localSec.type === "services_grid" ? "Leistungs-Karten" : "Manifest-Punkte";
            return (
              <div className="mt-5 space-y-3" data-testid={`list-editor-${localSec.type}`}>
                <div className="text-xs font-bold uppercase tracking-wider opacity-70">{label}</div>
                {localSec.type === "services_grid" && (
                  <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="media-bw-toggle">
                    <input type="checkbox" className="w-4 h-4 accent-[var(--accent)]" checked={localSec.media_bw !== false} onChange={(e) => setBaseField({ media_bw: e.target.checked })} />
                    <span className="brut-label !mb-0">Karten-Bilder in Schwarzweiß anzeigen</span>
                  </label>
                )}
                {list.map((it, idx) => (
                  <div key={idx} className="border border-[var(--line-soft)] p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] uppercase opacity-50">#{idx + 1}</span>
                      <div className="flex gap-3 items-center">
                        <button type="button" onClick={() => moveListItem(key, idx, -1)} className="text-xs">↑</button>
                        <button type="button" onClick={() => moveListItem(key, idx, 1)} className="text-xs">↓</button>
                        <button type="button" onClick={() => removeListItem(key, idx)} className="text-red-600"><Trash2 size={12} /></button>
                      </div>
                    </div>
                    {localSec.type === "stats" && (
                      <>
                        <div className="grid grid-cols-2 gap-2">
                          <input className="brut-input" placeholder="Wert (z.B. 5–10)" value={it.value || ""} onChange={e => updateList(key, idx, { value: e.target.value })} data-testid={`stat-value-${idx}`} />
                          <input className="brut-input" placeholder="Einheit (z.B. Tage / %)" value={it.unit || ""} onChange={e => updateList(key, idx, { unit: e.target.value })} />
                        </div>
                        <input className="brut-input" placeholder="Label" value={it.label || ""} onChange={e => updateList(key, idx, { label: e.target.value })} />
                      </>
                    )}
                    {localSec.type === "workflow_steps" && (
                      <>
                        <div className="grid grid-cols-[80px_1fr] gap-2">
                          <input className="brut-input" placeholder="Nr." value={it.no || ""} onChange={e => updateList(key, idx, { no: e.target.value })} />
                          <input className="brut-input" placeholder="Titel" value={it.title || ""} onChange={e => updateList(key, idx, { title: e.target.value })} data-testid={`step-title-${idx}`} />
                        </div>
                        <textarea rows={2} className="brut-textarea" placeholder="Text" value={it.text || ""} onChange={e => updateList(key, idx, { text: e.target.value })} />
                        <MediaPicker label="Icon" value={it.icon_media_id} onChange={v => updateList(key, idx, { icon_media_id: v })} />
                      </>
                    )}
                    {localSec.type === "manifest" && (
                      <>
                        <input className="brut-input" placeholder="Titel" value={it.title || ""} onChange={e => updateList(key, idx, { title: e.target.value })} />
                        <textarea rows={2} className="brut-textarea" placeholder="Text" value={it.text || ""} onChange={e => updateList(key, idx, { text: e.target.value })} />
                      </>
                    )}
                    {localSec.type === "services_grid" && (
                      <>
                        <input className="brut-input" placeholder="Titel" value={it.title || ""} onChange={e => updateList(key, idx, { title: e.target.value })} data-testid={`service-title-${idx}`} />
                        <textarea rows={2} className="brut-textarea" placeholder="Text" value={it.text || ""} onChange={e => updateList(key, idx, { text: e.target.value })} />
                        <input className="brut-input" placeholder="Link (z.B. /praxis-check) — optional" value={it.url || ""} onChange={e => updateList(key, idx, { url: e.target.value })} />
                        <MediaPicker label="Karten-Bild" value={it.image_media_id} onChange={v => updateList(key, idx, { image_media_id: v })} />
                      </>
                    )}
                  </div>
                ))}
                <button type="button" onClick={() => addListItem(key, localSec.type === "stats" ? { value: "", unit: "", label: "" } : localSec.type === "workflow_steps" ? { no: String(list.length + 1).padStart(2, "0"), title: "", text: "", icon_media_id: null } : localSec.type === "services_grid" ? { title: "", text: "", url: "", image_media_id: null } : { title: "", text: "" })} className="brut-btn brut-btn-ghost !py-2 !px-3 text-xs" data-testid={`add-list-item-${localSec.type}`}>
                  <Plus size={12} /> Element hinzufügen
                </button>
              </div>
            );
          })()}
          <details className="mt-5">
            <summary className="text-xs font-bold uppercase tracking-wider opacity-70 cursor-pointer">Erweiterte Inhalte als JSON</summary>
            <textarea rows={10} className="brut-textarea mt-2 text-xs"
              value={JSON.stringify(content, null, 2)}
              onChange={(e) => { try { setContent(JSON.parse(e.target.value)); } catch (_) {} }}
            />
          </details>
        </div>
      )}
    </div>
  );
}

export default function PageEditor({ page, onSaved, onClose }) {
  const [draft, setDraft] = React.useState(page);
  const [lang, setLang] = React.useState("de");
  const [busy, setBusy] = React.useState(false);
  const [metaDirty, setMetaDirty] = React.useState(false);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [templatesOpen, setTemplatesOpen] = React.useState(false);
  const [templates, setTemplates] = React.useState([]);

  React.useEffect(() => { setDraft(page); setMetaDirty(false); }, [page]);
  React.useEffect(() => { api.listTemplates().then(setTemplates).catch(() => {}); }, []);
  React.useEffect(() => {
    if (!metaDirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [metaDirty]);

  const updateSection = async (sec) => {
    const updated = await api.updateSection(draft.id, sec.id, sec);
    setDraft(d => ({ ...d, sections: d.sections.map(s => s.id === sec.id ? updated : s) }));
    toast.success("Gespeichert", { duration: 1200 });
  };
  const deleteSection = async (sid) => {
    if (!window.confirm("Sektion wirklich löschen?")) return;
    await api.deleteSection(draft.id, sid);
    setDraft(d => ({ ...d, sections: d.sections.filter(s => s.id !== sid) }));
  };
  const dupSection = async (sid) => {
    await api.duplicateSection(draft.id, sid);
    const fresh = await api.adminPage(draft.id);
    setDraft(fresh);
  };
  const move = async (idx, dir) => {
    const arr = [...draft.sections];
    const tgt = idx + dir;
    if (tgt < 0 || tgt >= arr.length) return;
    [arr[idx], arr[tgt]] = [arr[tgt], arr[idx]];
    const normalized = arr.map((section, order) => ({ ...section, order }));
    setDraft({ ...draft, sections: normalized });
    await api.reorderSections(draft.id, normalized.map(s => s.id));
  };
  const reorderTo = async (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const arr = [...draft.sections];
    const [moved] = arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, moved);
    const normalized = arr.map((section, order) => ({ ...section, order }));
    setDraft({ ...draft, sections: normalized });
    await api.reorderSections(draft.id, normalized.map(s => s.id));
  };
  const [dragIdx, setDragIdx] = React.useState(null);
  const [overIdx, setOverIdx] = React.useState(null);
  // Drag is only "armed" when started from the GripVertical handle, so selecting
  // text inside a section no longer drags the whole card around.
  const dragArmedRef = React.useRef(false);
  React.useEffect(() => {
    const reset = () => { dragArmedRef.current = false; };
    window.addEventListener("mouseup", reset);
    return () => window.removeEventListener("mouseup", reset);
  }, []);
  const [expandedId, setExpandedId] = React.useState(null);
  const addSection = async (type) => {
    const newSec = await api.addSection(draft.id, { type, content: { de: { eyebrow: "", headline: "Neue Sektion", body: "" }, en: {} }, enabled: true });
    setDraft(d => ({ ...d, sections: [...d.sections, newSec] }));
    setPickerOpen(false);
  };
  const applyTemplate = async (key, mode) => {
    if (!window.confirm(mode === "replace" ? "Bestehende Sektionen ERSETZEN durch Template?" : "Template-Sektionen am Ende anhängen?")) return;
    setBusy(true);
    try {
      await api.applyTemplate(key, draft.id, mode);
      const fresh = await api.adminPage(draft.id);
      setDraft(fresh);
      toast.success("Template angewendet");
      setTemplatesOpen(false);
    } catch { toast.error("Fehler beim Anwenden"); }
    finally { setBusy(false); }
  };
  const savePageMeta = async () => {
    setBusy(true);
    try {
      // On the workflow page, the page-level summary is also the visible
      // introductory copy of the first hero. Keep both fields in sync so the
      // editor cannot show one text while the public page renders another.
      if (draft.slug === "workflow") {
        const hero = (draft.sections || []).find(section => section.type === "hero");
        if (hero) {
          const syncedContent = { ...(hero.content || {}) };
          for (const code of ["de", "en"]) {
            const summary = draft[`seo_${code}`]?.ai_summary;
            if (typeof summary === "string" && summary.trim()) {
              syncedContent[code] = { ...(syncedContent[code] || {}), sub: summary.trim() };
            }
          }
          await api.updateSection(draft.id, hero.id, { ...hero, content: syncedContent });
        }
      }
      const pageMeta = {
        title_de: draft.title_de,
        title_en: draft.title_en,
        nav_label_de: draft.nav_label_de,
        nav_label_en: draft.nav_label_en,
        slug: draft.slug,
        nav_visible: draft.nav_visible,
        seo_de: draft.seo_de || {},
        seo_en: draft.seo_en || {},
      };
      const r = await api.updatePage(draft.id, pageMeta);
      setDraft(r);
      setMetaDirty(false);
      toast.success("Seitendaten gespeichert");
      onSaved?.(r);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Seitendaten konnten nicht gespeichert werden");
    } finally { setBusy(false); }
  };
  const genSeo = async () => {
    setBusy(true);
    try {
      const seo = await api.generateSeo(draft.id, lang);
      setDraft(d => ({ ...d, [`seo_${lang}`]: { ...(d[`seo_${lang}`]||{}), ...seo } }));
      setMetaDirty(true);
      toast.success("SEO generiert");
    } catch (e) { toast.error("SEO-Generation fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  const translateAll = async () => {
    setBusy(true);
    try {
      // Translate and persist every page-level field, including the SEO/GEO
      // summary that doubles as the visible workflow intro.
      const pageTranslation = await api.translate({
        source: "de",
        target: "en",
        obj: {
          title: draft.title_de || "",
          nav: draft.nav_label_de || "",
          seo_title: draft.seo_de?.title || "",
          seo_description: draft.seo_de?.description || "",
          ai_summary: draft.seo_de?.ai_summary || "",
        },
      });
      const translatedPage = pageTranslation.obj || {};
      const firstHeroId = (draft.sections || []).find(section => section.type === "hero")?.id;
      await api.updatePage(draft.id, {
        title_en: translatedPage.title || draft.title_en,
        nav_label_en: translatedPage.nav || draft.nav_label_en,
        seo_en: {
          ...(draft.seo_en || {}),
          title: translatedPage.seo_title || draft.seo_en?.title || "",
          description: translatedPage.seo_description || draft.seo_en?.description || "",
          ai_summary: translatedPage.ai_summary || draft.seo_en?.ai_summary || "",
        },
      });
      // Translate each section's de content
      for (const sec of draft.sections) {
        const de = sec.content?.de || {};
        if (Object.keys(de).length) {
          const tr = await api.translate({ source: "de", target: "en", obj: de });
          const translatedContent = { ...(tr.obj || {}) };
          if (draft.slug === "workflow" && sec.id === firstHeroId && translatedPage.ai_summary) {
            translatedContent.sub = translatedPage.ai_summary;
          }
          const updated = { ...sec, content: { ...(sec.content||{}), en: translatedContent } };
          await api.updateSection(draft.id, sec.id, updated);
        }
      }
      const fresh = await api.adminPage(draft.id);
      setDraft(fresh);
      setMetaDirty(false);
      toast.success("Seite und alle Sektionen wurden vollständig nach EN übersetzt");
    } catch (e) { toast.error("Übersetzung fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="page-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="brut-tag mb-2">SEITE / {draft.slug}</div>
          <h1 className="brut-h2">{draft.title_de}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex border-2 border-[var(--ink)]">
            <button onClick={() => setLang("de")} className={`px-3 py-2 font-mono text-xs uppercase ${lang === "de" ? "bg-[var(--ink)] text-white" : ""}`}>DE</button>
            <button onClick={() => setLang("en")} className={`px-3 py-2 font-mono text-xs uppercase ${lang === "en" ? "bg-[var(--ink)] text-white" : ""}`}>EN</button>
          </div>
          <button onClick={translateAll} disabled={busy} className="brut-btn brut-btn-ghost !py-2 !px-3 text-xs"><Sparkles size={12}/> Auto-Übersetzen</button>
          <button onClick={genSeo} disabled={busy} className="brut-btn brut-btn-ghost !py-2 !px-3 text-xs"><Sparkles size={12}/> SEO generieren</button>
          <button onClick={savePageMeta} disabled={busy || !metaDirty} className={`brut-btn !py-2 !px-3 text-xs ${metaDirty ? "" : "brut-btn-ghost"}`}><Save size={12}/> Seitendaten speichern</button>
          <button onClick={onClose} className="font-mono text-xs uppercase underline">schließen</button>
        </div>
      </div>

      <div onChangeCapture={() => setMetaDirty(true)} className="grid grid-cols-1 md:grid-cols-2 gap-4 border-2 border-[var(--ink)] p-5">
        <Field label="Titel (DE)" value={draft.title_de} onChange={(v) => setDraft({ ...draft, title_de: v })} />
        <Field label="Titel (EN)" value={draft.title_en} onChange={(v) => setDraft({ ...draft, title_en: v })} />
        <Field label="Nav-Label (DE)" value={draft.nav_label_de} onChange={(v) => setDraft({ ...draft, nav_label_de: v })} />
        <Field label="Nav-Label (EN)" value={draft.nav_label_en} onChange={(v) => setDraft({ ...draft, nav_label_en: v })} />
        <Field label="Slug" value={draft.slug} onChange={(v) => setDraft({ ...draft, slug: v })} />
        <div>
          <label className="brut-label">In Navigation anzeigen</label>
          <select className="brut-select" value={String(draft.nav_visible)} onChange={(e) => setDraft({ ...draft, nav_visible: e.target.value === "true" })}>
            <option value="true">Ja</option><option value="false">Nein</option>
          </select>
        </div>
        <Field label={`SEO-Titel (${lang.toUpperCase()})`} value={draft[`seo_${lang}`]?.title} onChange={(v) => setDraft({ ...draft, [`seo_${lang}`]: { ...(draft[`seo_${lang}`]||{}), title: v } })} />
        <Field label={`Meta-Description (${lang.toUpperCase()})`} value={draft[`seo_${lang}`]?.description} onChange={(v) => setDraft({ ...draft, [`seo_${lang}`]: { ...(draft[`seo_${lang}`]||{}), description: v } })} multiline />
        <div>
          <Field label={draft.slug === "workflow" ? "Introtext / KI-Zusammenfassung (wird im ersten Hero angezeigt)" : "KI-Zusammenfassung (nur SEO/GEO – nicht auf der Seite sichtbar)"} value={draft[`seo_${lang}`]?.ai_summary} onChange={(v) => setDraft({ ...draft, [`seo_${lang}`]: { ...(draft[`seo_${lang}`]||{}), ai_summary: v } })} multiline />
          <p className="mt-2 text-xs opacity-65">{draft.slug === "workflow" ? "Beim Speichern der Seitendaten wird dieser Text automatisch als sichtbarer Untertext in die erste Hero-Sektion übernommen." : "Sichtbare Texte werden direkt in den darunterliegenden Sektionen bearbeitet, z. B. in „Body / Untertext“ der Hero-Sektion."}</p>
        </div>
        <div className="md:col-span-2 mt-2 pt-4 border-t border-[var(--line)] flex flex-wrap items-center justify-between gap-3">
          <span className={`text-xs font-bold ${metaDirty ? "text-orange-600" : "text-green-600"}`}>
            {metaDirty ? "● Nicht gespeicherte Seitendaten" : "✓ Seitendaten gespeichert"}
          </span>
          <button type="button" onClick={savePageMeta} disabled={busy || !metaDirty} className={`brut-btn !py-2.5 !px-4 text-xs ${metaDirty ? "" : "brut-btn-ghost"}`} data-testid="save-page-meta-bottom">
            <Save size={14}/> {busy ? "Speichert …" : "Seitendaten speichern"}
          </button>
        </div>
      </div>

      {/* Add section + Templates */}
      <div className="border-2 border-[var(--ink)] p-4 flex flex-wrap items-center gap-3">
        <button onClick={() => setPickerOpen(true)} className="brut-btn !py-2.5 !px-4 text-xs" data-testid="open-section-picker">
          <Plus size={14}/> Sektion hinzufügen
        </button>
        <button onClick={() => setTemplatesOpen(true)} className="brut-btn brut-btn-ghost !py-2.5 !px-4 text-xs">
          <Sparkles size={14}/> Vorlage anwenden
        </button>
        <span className="text-xs opacity-60">{draft.sections.length} Sektionen auf dieser Seite</span>
      </div>

      {pickerOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(22,22,24,0.55)", backdropFilter: "blur(8px)" }} onClick={() => setPickerOpen(false)}>
          <div className="admin-card max-w-4xl w-full max-h-[85vh] overflow-y-auto p-6 md:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="brut-tag mb-3">Sektion auswählen</div>
                <h2 className="brut-h3">Welcher Section-Typ?</h2>
              </div>
              <button onClick={() => setPickerOpen(false)} className="text-2xl leading-none">×</button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {SECTION_TYPES.map(t => (
                <button key={t.value} onClick={() => addSection(t.value)} data-testid={`add-section-${t.value}`} className="text-left p-5 rounded-2xl admin-card lift-on-hover">
                  <div className="font-extrabold text-base">{t.label}</div>
                  <div className="text-xs opacity-70 mt-1">{t.desc}</div>
                  <div className="font-mono text-[10px] opacity-50 mt-2">type: {t.value}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {templatesOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(22,22,24,0.55)", backdropFilter: "blur(8px)" }} onClick={() => setTemplatesOpen(false)}>
          <div className="admin-card max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6 md:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="brut-tag mb-3">Vorlagen</div>
                <h2 className="brut-h3">Fertige Section-Bibliotheken</h2>
              </div>
              <button onClick={() => setTemplatesOpen(false)} className="text-2xl leading-none">×</button>
            </div>
            <div className="space-y-4">
              {templates.map(tpl => (
                <div key={tpl.key} className="p-5 rounded-2xl border border-[var(--line)] flex items-start justify-between gap-4">
                  <div>
                    <div className="font-extrabold text-lg">{tpl.label}</div>
                    <div className="text-sm opacity-70">{tpl.description}</div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => applyTemplate(tpl.key, "append")} className="brut-btn brut-btn-ghost !py-2 !px-3 text-xs">Anhängen</button>
                    <button onClick={() => applyTemplate(tpl.key, "replace")} className="brut-btn !py-2 !px-3 text-xs">Ersetzen</button>
                  </div>
                </div>
              ))}
              {!templates.length && <div className="text-sm opacity-60">Keine Vorlagen verfügbar</div>}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {(draft.sections || []).map((sec, idx) => (
          <div
            key={sec.id}
            draggable
            onDragStart={(e) => {
              if (!dragArmedRef.current) { e.preventDefault(); return; }
              setDragIdx(idx); e.dataTransfer.effectAllowed = "move";
            }}
            onDragOver={(e) => { if (dragIdx === null) return; e.preventDefault(); setOverIdx(idx); }}
            onDragLeave={() => setOverIdx(null)}
            onDrop={(e) => { e.preventDefault(); if (dragIdx !== null) reorderTo(dragIdx, idx); setDragIdx(null); setOverIdx(null); dragArmedRef.current = false; }}
            onDragEnd={() => { setDragIdx(null); setOverIdx(null); dragArmedRef.current = false; }}
            className={`transition-all ${overIdx === idx && dragIdx !== idx ? "scale-[1.01] -translate-y-1" : ""} ${dragIdx === idx ? "opacity-40" : ""}`}
          >
            <SectionEditor
              sec={sec}
              lang={lang}
              expanded={expandedId === sec.id}
              onToggle={() => setExpandedId(expandedId === sec.id ? null : sec.id)}
              onChange={updateSection}
              onDelete={() => deleteSection(sec.id)}
              onDuplicate={() => dupSection(sec.id)}
              onDragHandleDown={() => { dragArmedRef.current = true; }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
